import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from './NotificationContext';
import { db } from "../firebase";
import { collection, query, where, onSnapshot, doc, setDoc, deleteDoc, updateDoc, increment } from "firebase/firestore";

const CounterContext = createContext();

export const CounterProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, isLoggedIn } = useAuth();
  const { notify } = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoggedIn || !currentUser) {
      setCartItems([]);
      setWishlistItems([]);
      setLoading(false);
      return;
    }

    const userId = currentUser.uid;

    const cartQuery = query(collection(db, "cart"), where("user_id", "==", userId));
    const unsubCart = onSnapshot(cartQuery, (snapshot) => {
      setCartItems(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      setLoading(false);
    });

    const wishQuery = query(collection(db, "wishlist"), where("user_id", "==", userId));
    const unsubWish = onSnapshot(wishQuery, (snapshot) => {
      setWishlistItems(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })));
    });

    return () => {
      unsubCart();
      unsubWish();
    };
  }, [isLoggedIn, currentUser]);

  const addToCart = async (product) => {
    if (!isLoggedIn) {
      notify("Please log in to continue", "info", {
        subtext: "Sign in to add items to your cart",
      });
      return;
    }

    // Generate specific ID for this product + size combination
    // Format: UID_PID_SIZE (if size exists) or UID_PID (if no size)
    const sizeSuffix = product.selectedSize ? `_${product.selectedSize}` : '';
    const cartId = `${currentUser.uid}_${product.product_id || product.id}${sizeSuffix}`;

    // Check if exists
    // We can check by ID directly since we constructed the unique ID
    const existing = cartItems.find(item => item.id === cartId);

    if (existing) {
      // Just increment quantity
      await updateCartQuantity(existing.id, existing.quantity + 1);
      notify("Quantity updated", "cart", {
        subtext: product.name || product.title || undefined,
      });
      return;
    }

    try {
      await setDoc(doc(db, "cart", cartId), {
        ...product,
        user_id: currentUser.uid,
        quantity: 1,
        addedAt: new Date()
      });
      const subtextParts = [product.name || product.title, product.selectedSize].filter(Boolean);
      notify("Added to cart", "cart", {
        subtext:   subtextParts.length ? subtextParts.join(' · ') : undefined,
        thumbnail: product.image || product.img || product.thumbnail || undefined,
        cta:       "View Cart",
        ctaAction: () => navigate('/cart'),
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const removeFromCart = async (cartItemId) => {
    if (!isLoggedIn) return;
    // We expect the direct Firestore Document ID here
    try {
      await deleteDoc(doc(db, "cart", cartItemId));
      notify("Removed from cart", "remove");
    } catch (error) {
      console.error("Error removing from cart:", error);
    }
  };

  const updateCartQuantity = async (cartItemId, newQuantity) => {
    if (!isLoggedIn) return;
    if (newQuantity < 1) return;

    try {
      await updateDoc(doc(db, "cart", cartItemId), { quantity: newQuantity });
    } catch (error) {
      console.error("Error updating quantity:", error);
    }
  }

  const addToWishlist = async (product) => {
    if (!isLoggedIn) {
      notify("Please log in to continue", "info", {
        subtext: "Sign in to save items to your wishlist",
      });
      return;
    }
    const wishId = `${currentUser.uid}_${product.product_id || product.id}`;
    try {
      await setDoc(doc(db, "wishlist", wishId), {
        ...product,
        user_id: currentUser.uid,
        addedAt: new Date()
      });
      notify("Saved to wishlist", "wishlist", {
        subtext:   product.name || product.title || undefined,
        thumbnail: product.image || product.img || product.thumbnail || undefined,
        cta:       "View Wishlist",
        ctaAction: () => navigate('/wishlist'),
      });
    } catch (error) {
      console.error("Error adding to wishlist:", error);
    }
  };

  const removeFromWishlist = async (productId) => {
    if (!isLoggedIn) return;
    const wishId = productId.startsWith(`${currentUser.uid}_`)
      ? productId
      : `${currentUser.uid}_${productId}`;
    try {
      await deleteDoc(doc(db, "wishlist", wishId));
      notify("Removed from wishlist", "remove");
    } catch (error) {
      console.error("Error removing from wishlist:", error);
    }
  };

  const clearCart = async () => {
    if (!isLoggedIn) return;
    try {
      // Loop through all cart items and delete them
      // In a production app, use writeBatch for atomicity
      const deletionPromises = cartItems.map(item =>
        deleteDoc(doc(db, "cart", item.id))
      );
      await Promise.all(deletionPromises);
    } catch (error) {
      console.error("Error clearing cart:", error);
    }
  };

  const getCartCount = cartItems.reduce((acc, item) => acc + Number(item.quantity || 1), 0);

  return (
    <CounterContext.Provider value={{
      cartItems,
      wishlistItems,
      loading,
      getCartCount,
      addToCart,
      addToWishlist,
      removeFromCart,
      removeFromWishlist,
      updateCartQuantity,
      clearCart
    }}>
      {children}
    </CounterContext.Provider>
  );
};

export const useCounter = () => useContext(CounterContext);